import { Fragment, useCallback, useEffect, useState } from 'react';
import { pmGet, pmPost, type PmProject } from '../api';
import './WorkItemAssignment.css';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Work Item Assignment.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/assignment.ejs`.
 *
 * WHAT IT DOES, in the source's words: put names against the work. For every work item
 * marked in scope on Work Item Definition — program-wide (Global) and per opportunity, per
 * phase and per stream — assign one or more people who own producing it.
 *
 * A TREE GRID, not nested tables: one table whose rows are scope → phase/MVP → work item,
 * depth shown by indent, with the caret in its own 44px indicator column. The source's note
 * saying so is carried into the CSS.
 *
 * WATERFALL GROUPS BY PHASE, AGILE BY MVP WAVE. The server decides which: for an Agile
 * opportunity `/assignment/program-data` rewrites each item's `phase` to `mvp-<seq>` and
 * returns phase objects carrying that wave's own start and end. `phaseDateMap` therefore
 * prefers a scope's own phase window and falls back to the program-wide Waterfall
 * `phase_starts` / `phase_ends` — which is why it is a per-scope resolver rather than one
 * global map.
 *
 * DATES ARE DEFAULTED ON LOAD, once, exactly as the source's `fillDates` does: any assignee
 * with no start or end takes its work item's phase window. Doing it on load rather than at
 * render keeps a deliberately-cleared date cleared.
 *
 * SAVE POSTS EVERY ITEM in the program, Global and all opportunities, so a collapsed scope
 * is never dropped. Rows with neither a name nor an email are discarded by the server, not
 * stored blank.
 */

interface Assignee {
  name?: string;
  email?: string;
  location?: string;
  start_date?: string;
  end_date?: string;
}

interface WorkItem {
  id: number;
  phase: string;
  name: string;
  stream?: string | null;
  owner?: string | null;
  work_item_type?: string | null;
  assignees?: Assignee[];
}

interface PhaseRef {
  key: string;
  label: string;
  start?: string;
  end?: string;
  mode?: string;
}

interface Scope {
  opportunity_id?: string;
  name?: string;
  methodology?: string;
  phases?: PhaseRef[];
  items: WorkItem[];
}

interface Person {
  name?: string;
  email?: string;
  location?: string;
}

interface ProgramData {
  success?: boolean;
  error?: string;
  people?: Person[];
  phase_starts?: Record<string, string>;
  phase_ends?: Record<string, string>;
  global: Scope;
  opportunities: Scope[];
}

/* The source's own constants, verbatim. */
const PHASE_LABEL: Record<string, string> = {
  Assessment_Planning: 'Assessment & Planning',
  Design_Architecture: 'Design & Architecture',
  Development: 'Development',
  Testing_Validation: 'Testing & Validation',
  GoLive_Optimization: 'Go-Live & Optimization',
  Agile_Delivery: 'Agile Delivery',
};
const PHASE_COLORS: Record<string, string> = {
  Assessment_Planning: '#90caf9',
  Design_Architecture: '#ce93d8',
  Development: '#a5d6a7',
  Testing_Validation: '#ffcc80',
  GoLive_Optimization: '#ef9a9a',
  Agile_Delivery: '#80cbc4',
};
const MVP_COLORS = ['#5c6bc0', '#26a69a', '#ec407a', '#ffa726', '#8d6e63', '#78909c'];

function mvpColor(key: string): string | null {
  const m = /^mvp-(\d+)$/.exec(key);
  return m ? (MVP_COLORS[Number(m[1]) % MVP_COLORS.length] ?? null) : null;
}

/** `assigned(it)` — a work item counts as staffed once any row carries a name or an email. */
const assigned = (it: WorkItem) =>
  (it.assignees ?? []).some((a) => (a.name || '').trim() || (a.email || '').trim());

const countAssigned = (it: WorkItem) =>
  (it.assignees ?? []).filter((a) => (a.name || '').trim() || (a.email || '').trim()).length;

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/assignment.ejs`,
 * its `assignmentHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const ASSIGNMENT_HELP: HelpContent = {
    title: 'Work Item Assignment',
    icon: '🧑‍💼',
    purpose: "Put <strong>names against the work</strong>. For every work item you marked <strong>in scope</strong> on Work Item Definition &mdash; project&#8209;wide (Global) and <strong>per opportunity</strong>, <strong>per phase</strong> and <strong>per stream</strong> &mdash; assign one or more <strong>people</strong> (name + email) who own producing it. This staffing feeds the <strong>Control Center</strong>, budget/resource tracking and the status reports.",
    doHere: [
      "An opportunity whose methodology is <strong>None</strong> has <strong>no phases</strong> &mdash; it appears here as a single <strong>Delivery</strong> row covering its whole window, and is fully assignable just like any other initiative.",
      "Pick a <strong>Project</strong>; its Global and per&#8209;opportunity in&#8209;scope work items load, all collapsed. (Only work items in scope appear &mdash; set scope on Work Item Definition first.)",
      "Open <strong>🌐 Global</strong> for one flat list &mdash; project&#8209;level work items have <strong>no phases</strong>. Open an <strong>opportunity</strong> &rarr; a group: <strong>Waterfall</strong> opportunities group by phase, <strong>Agile</strong> ones by <strong>MVP wave</strong> (each shows its date window), and one set to <strong>None</strong> is a single flat group. Every work item shows its <strong>stream</strong> chip and <strong>typical owner</strong>.",
      "<strong>Add a person</strong> &mdash; pick from the project team via <em>&#65291; team</em>, or type a name + email. Multiple people per work item are fine.",
      "Adjust each person's <strong>Start</strong> / <strong>End</strong> dates if needed &mdash; they default to the work item's <strong>phase</strong> dates (Waterfall) or its <strong>MVP wave</strong> window (Agile) &mdash; then <strong>Save</strong>."
    ],
    fits: 'Work Item Definition &rarr; <strong>Work Item Assignment</strong> &rarr; RACI Definition &rarr; Execution Control Center',
    firstTime: "Pick a project, expand an opportunity and a phase, click <strong>&#65291; team</strong> on a work item to pick a member, then Save.",
    sections: [
      {
        label: 'How assignment is organized',
        type: 'defs',
        items: [
          { term: 'Global', color: '#6a1b9a', def: "Project&#8209;level work items (charter, governance&hellip;) staffed once for the whole project. They belong to no opportunity, so they have <strong>no phases</strong> and are listed flat &mdash; the same treatment as an opportunity set to <strong>None</strong>. This screen shows exactly what <strong>Work Item Definition</strong> shows." },
          { term: 'Opportunity', color: '#1976d2', def: "Each opportunity's own work items are staffed independently &mdash; Waterfall phases or Agile items, per its methodology." },
          { term: 'Stream', color: '#1565c0', def: "A work item's <strong>stream</strong> is shown as a chip. The same work item can be staffed differently across streams (e.g. Order to Cash vs Record to Report)." },
          { term: 'Person', color: '#2e7d32', def: "One or more people per work item &mdash; each with name, email, optional location, and Start / End dates." }
        ]
      },
      {
        label: 'Picking people & dates',
        type: 'html',
        html: "The <strong>&#65291; team</strong> dropdown lists the project's <strong>stream members</strong> (from Stream Definition); choosing one fills their name and email and then shows who is assigned. You can also type a name/email directly for someone outside the team. <strong>Start</strong> and <strong>End</strong> default to the work item's <strong>phase</strong> window (Waterfall) or its <strong>MVP wave</strong> window (Agile, from the Agile timeline on Definition) and can be overridden."
      },
      {
        label: 'When work items are added or removed',
        type: 'html',
        html: "This screen always mirrors what is <strong>in scope</strong> on <strong>Work Item Definition</strong>.<br><br><strong>A work item is added</strong> &mdash; when someone marks a new work item in scope on Selection (a catalog item or a custom one), it <strong>appears here</strong> for its phase and stream, waiting to be staffed. Assign the people who will produce it.<br><br><strong>A work item is removed</strong> &mdash; if a work item you have already staffed is un&#8209;scoped on Selection, the app warns the remover that it is assigned; on save the work item <strong>and the assignments you made here are permanently deleted</strong>. So an item disappearing from this screen means it was taken out of scope &mdash; re&#8209;add it on Work Item Definition to bring it (and fresh assignments) back."
      },
      {
        label: 'Assignment vs. RACI',
        type: 'html',
        html: "<strong>Assignment</strong> records <em>who works on</em> a work item (the staffing). <strong>RACI</strong> (next screen) records the <em>decision roles</em> &mdash; who is Responsible, Accountable, Consulted and Informed across the standard team roles."
      }
    ],
    links: [
      { label: 'Back: Work Item Definition →', href: '/project_management/program-management/scope-ownership/work-item-definition' },
      { label: 'Next: RACI Definition →', href: '/project_management/program-management/scope-ownership/raci-definition' }
    ]
  };

export function WorkItemAssignment() {
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [projectId, setProjectId] = useState('');
  const [prog, setProg] = useState<ProgramData | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [phaseExpanded, setPhaseExpanded] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [saveLabel, setSaveLabel] = useState('Save');
  const [emptyReason, setEmptyReason] = useState<'no-project' | 'no-items'>('no-project');
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const d = await pmGet<{ projects: PmProject[] }>('/deliverables/projects');
        setProjects(d.projects ?? []);
      } catch {
        /* the source logs and leaves the picker empty */
      }
    })();
  }, []);

  /** `phaseDateMap(scope)` — a scope's own window wins over the Waterfall map. */
  const phaseDates = useCallback(
    (scope: Scope, key: string): { s: string; e: string } => {
      const own = (scope.phases ?? []).find((ph) => ph.key === key && (ph.start || ph.end));
      if (own) return { s: own.start || '', e: own.end || '' };
      return { s: prog?.phase_starts?.[key] || '', e: prog?.phase_ends?.[key] || '' };
    },
    [prog],
  );

  const onSelectProject = async (id: string) => {
    setProjectId(id);
    setNotice(null);
    if (!id) {
      setProg(null);
      setEmptyReason('no-project');
      return;
    }
    try {
      const d = await pmGet<ProgramData>(
        `/assignment/program-data?delivery_project_id=${encodeURIComponent(id)}`,
      );
      setPeople(d.people ?? []);

      // `fillDates` — default each assignee's dates to its work item's phase window.
      const starts = d.phase_starts ?? {};
      const ends = d.phase_ends ?? {};
      const fill = (scope: Scope) => {
        const own = (key: string) => (scope.phases ?? []).find((ph) => ph.key === key && (ph.start || ph.end));
        (scope.items ?? []).forEach((it) => {
          const w = own(it.phase);
          const s = w ? w.start || '' : starts[it.phase] || '';
          const e = w ? w.end || '' : ends[it.phase] || '';
          (it.assignees ?? []).forEach((a) => {
            if (!a.start_date) a.start_date = s;
            if (!a.end_date) a.end_date = e;
          });
        });
      };
      fill(d.global);
      (d.opportunities ?? []).forEach(fill);

      setExpanded(new Set());
      setPhaseExpanded(new Set());
      setDirty(false);
      setSaveLabel('Save');

      // Nothing in scope anywhere: the source swaps the empty state's wording and hides
      // the toolbar actions, rather than rendering an empty grid.
      const hasAny =
        d.global.items.length > 0 || (d.opportunities ?? []).some((o) => o.items.length > 0);
      setEmptyReason(hasAny ? 'no-project' : 'no-items');
      setProg(hasAny ? d : null);
    } catch {
      setNotice('Failed to load assignments');
    }
  };

  const scopeIdOf = (op?: Scope) => (op ? `opp:${op.opportunity_id}` : 'global');

  const scopeFor = (scopeId: string): Scope | undefined => {
    if (!prog) return undefined;
    if (scopeId === 'global') return prog.global;
    return prog.opportunities.find((o) => `opp:${o.opportunity_id}` === scopeId);
  };

  /** Mutating a nested item — cloned down the path React needs to see a change. */
  const updateItem = (scopeId: string, itemId: number, fn: (it: WorkItem) => WorkItem) => {
    setProg((cur) => {
      if (!cur) return cur;
      const applyTo = (scope: Scope): Scope => ({
        ...scope,
        items: scope.items.map((it) => (it.id === itemId ? fn(it) : it)),
      });
      if (scopeId === 'global') return { ...cur, global: applyTo(cur.global) };
      return {
        ...cur,
        opportunities: cur.opportunities.map((o) =>
          `opp:${o.opportunity_id}` === scopeId ? applyTo(o) : o,
        ),
      };
    });
    setDirty(true);
    setSaveLabel('Save');
  };

  const setAssignee = (
    scopeId: string,
    itemId: number,
    ai: number,
    field: keyof Assignee,
    val: string,
  ) =>
    updateItem(scopeId, itemId, (it) => ({
      ...it,
      assignees: (it.assignees ?? []).map((a, i) => (i === ai ? { ...a, [field]: val } : a)),
    }));

  const pickPerson = (scopeId: string, itemId: number, ai: number, pi: string) => {
    if (pi === '') return;
    const p = people[Number(pi)];
    if (!p) return;
    updateItem(scopeId, itemId, (it) => ({
      ...it,
      assignees: (it.assignees ?? []).map((a, i) =>
        i === ai
          ? {
              ...a,
              name: p.name || '',
              email: p.email || '',
              ...(p.location ? { location: p.location } : {}),
            }
          : a,
      ),
    }));
  };

  const addAssignee = (scopeId: string, itemId: number, phase: string) => {
    const scope = scopeFor(scopeId);
    const d = scope ? phaseDates(scope, phase) : { s: '', e: '' };
    updateItem(scopeId, itemId, (it) => ({
      ...it,
      assignees: [
        ...(it.assignees ?? []),
        { name: '', email: '', location: '', start_date: d.s, end_date: d.e },
      ],
    }));
  };

  const removeAssignee = (scopeId: string, itemId: number, ai: number) =>
    updateItem(scopeId, itemId, (it) => ({
      ...it,
      assignees: (it.assignees ?? []).filter((_, i) => i !== ai),
    }));

  const toggleScope = (scopeId: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(scopeId)) next.delete(scopeId);
      else next.add(scopeId);
      return next;
    });

  const togglePhase = (gid: string) =>
    setPhaseExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });

  const expandAll = (open: boolean) => {
    if (!prog) return;
    if (!open) {
      setExpanded(new Set());
      setPhaseExpanded(new Set());
      return;
    }
    const sc = new Set<string>();
    const ph = new Set<string>();
    if (prog.global.items.length) {
      sc.add('global');
      (prog.global.phases ?? []).forEach((p) => ph.add(`global::${p.key}`));
    }
    prog.opportunities.forEach((o) => {
      if (!o.items.length) return;
      const sid = `opp:${o.opportunity_id}`;
      sc.add(sid);
      (o.phases ?? []).forEach((p) => ph.add(`${sid}::${p.key}`));
    });
    setExpanded(sc);
    setPhaseExpanded(ph);
  };

  const totalAssigned = prog
    ? prog.global.items.filter(assigned).length +
      prog.opportunities.reduce((n, o) => n + o.items.filter(assigned).length, 0)
    : 0;

  const save = async () => {
    if (!projectId || !prog) return;
    const items: { id: number; assignees: Assignee[] }[] = [];
    prog.global.items.forEach((it) => items.push({ id: it.id, assignees: it.assignees ?? [] }));
    prog.opportunities.forEach((o) =>
      o.items.forEach((it) => items.push({ id: it.id, assignees: it.assignees ?? [] })),
    );
    setSaveLabel('Saving…');
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/assignment/program-save', {
        delivery_project_id: projectId,
        items,
      });
      if (d.success) {
        setDirty(false);
        setSaveLabel('Saved ✓');
        window.setTimeout(() => setSaveLabel('Save'), 1100);
      } else {
        setNotice(`Error: ${d.error || 'failed'}`);
        setSaveLabel('Save');
      }
    } catch {
      setNotice('Request failed');
      setSaveLabel('Save');
    }
  };

  const witBadge = (t?: string | null) => {
    const a = t === 'Activity';
    return (
      <span className={`wit-badge ${a ? 'a' : 'd'}`} title={a ? 'Activity' : 'Deliverable'}>
        {a ? 'A' : 'D'}
      </span>
    );
  };

  const assigneeEditor = (scopeId: string, it: WorkItem) => {
    const list = it.assignees ?? [];
    return (
      <div>
        {list.length === 0 ? <div className="asg-empty">No one assigned yet.</div> : null}
        {list.map((a, ai) => {
          // Which team member this row currently matches, by email then by name.
          let cur = -1;
          for (let i = 0; i < people.length; i++) {
            const p = people[i]!;
            if (
              (a.email && p.email && p.email.toLowerCase() === String(a.email).toLowerCase()) ||
              (!a.email && a.name && p.name === a.name)
            ) {
              cur = i;
              break;
            }
          }
          return (
            <div className="asg-row" key={ai}>
              <select
                className="asg-pick"
                title="Pick a team member"
                value={cur >= 0 ? String(cur) : ''}
                onChange={(e) => pickPerson(scopeId, it.id, ai, e.target.value)}
              >
                <option value="">＋ team…</option>
                {people.map((p, pi) => (
                  <option key={pi} value={String(pi)}>
                    {p.name || p.email}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="asg-in nm"
                list="pm-wia-people"
                placeholder="Name"
                value={a.name || ''}
                onChange={(e) => setAssignee(scopeId, it.id, ai, 'name', e.target.value)}
              />
              <input
                type="text"
                className="asg-in em"
                placeholder="email@company.com"
                value={a.email || ''}
                onChange={(e) => setAssignee(scopeId, it.id, ai, 'email', e.target.value)}
              />
              <input
                type="text"
                className="asg-in loc"
                placeholder="Location"
                value={a.location || ''}
                onChange={(e) => setAssignee(scopeId, it.id, ai, 'location', e.target.value)}
              />
              <span className="asg-dlab">Start</span>
              <input
                type="date"
                className="asg-date"
                value={a.start_date || ''}
                onChange={(e) => setAssignee(scopeId, it.id, ai, 'start_date', e.target.value)}
              />
              <span className="asg-dlab">End</span>
              <input
                type="date"
                className="asg-date"
                value={a.end_date || ''}
                onChange={(e) => setAssignee(scopeId, it.id, ai, 'end_date', e.target.value)}
              />
              <span
                className="asg-rm"
                title="Remove"
                onClick={() => removeAssignee(scopeId, it.id, ai)}
              >
                ✕
              </span>
            </div>
          );
        })}
        <button
          type="button"
          className="asg-add"
          onClick={() => addAssignee(scopeId, it.id, it.phase)}
        >
          + Add person
        </button>
      </div>
    );
  };

  /** One scope block: its own row, then a phase row and work items for each open phase. */
  const scopeRows = (scope: Scope, title: string, accent: string, op?: Scope) => {
    const scopeId = scopeIdOf(op);
    const open = expanded.has(scopeId);
    const total = scope.items.length;
    const done = scope.items.filter(assigned).length;
    const phases = scope.phases ?? [];
    return (
      <Fragment key={scopeId}>
        <tr className="scope-row" onClick={() => toggleScope(scopeId)}>
          <td className="ind">
            <i
              className="material-icons gcaret"
              style={{ transform: `rotate(${open ? 90 : 0}deg)` }}
            >
              chevron_right
            </i>
          </td>
          <td>
            <div className="grp-cell lvl0">
              <span className="dl-dot" style={{ background: accent }} />
              {title}
              {op ? <span className="dl-oppname">{op.name || ''}</span> : null}
              <span className="dl-pcount">
                {done} of {total} assigned
              </span>
              {op ? (
                <span className="grp-actions">
                  <span className={`meth-tag ${op.methodology === 'Agile' ? 'ag' : 'wf'}`}>
                    {op.methodology}
                  </span>
                </span>
              ) : null}
            </div>
          </td>
        </tr>

        {open && phases.length === 0 ? (
          <tr className="dlv-row">
            <td className="ind" />
            <td className="dlv-cell" style={{ color: '#b0bec5' }}>
              No in-scope work items.
            </td>
          </tr>
        ) : null}

        {open
          ? phases.map((phase) => {
              const gid = `${scopeId}::${phase.key}`;
              const pOpen = phaseExpanded.has(gid);
              const color = mvpColor(phase.key) || PHASE_COLORS[phase.key] || '#b0bec5';
              const label = PHASE_LABEL[phase.key] || phase.label;
              const rows = scope.items.filter((it) => it.phase === phase.key);
              const pDone = rows.filter(assigned).length;
              return (
                <Fragment key={gid}>
                  <tr className="phase-row" onClick={() => togglePhase(gid)}>
                    <td className="ind">
                      <i
                        className="material-icons gcaret sm"
                        style={{ transform: `rotate(${pOpen ? 90 : 0}deg)` }}
                      >
                        chevron_right
                      </i>
                    </td>
                    <td>
                      <div className="grp-cell lvl1">
                        <span className="ph-box" style={{ background: color }} />
                        {phase.mode === 'agile' ? '🔁 ' : ''}
                        {label}
                        {phase.start || phase.end ? (
                          <span className="ph-win">
                            {phase.start || ''} → {phase.end || ''}
                          </span>
                        ) : null}
                        <span className="dl-pcount">
                          {pDone} of {rows.length} assigned
                        </span>
                      </div>
                    </td>
                  </tr>
                  {pOpen
                    ? rows.map((it) => {
                        const n = countAssigned(it);
                        return (
                          <tr className="dlv-row" key={it.id}>
                            <td className="ind" />
                            <td className="dlv-cell">
                              <div className="dlv-h">
                                {witBadge(it.work_item_type)}
                                <span className="dlv-name">{it.name}</span>
                                {it.stream ? (
                                  <span className="chip stream">{it.stream}</span>
                                ) : null}
                                {it.owner ? (
                                  <span className="owner-hint">Typical owner: {it.owner}</span>
                                ) : null}
                                <span className="asg-count">{n ? `· ${n} assigned` : ''}</span>
                              </div>
                              {assigneeEditor(scopeId, it)}
                            </td>
                          </tr>
                        );
                      })
                    : null}
                </Fragment>
              );
            })
          : null}
      </Fragment>
    );
  };

  const hasRows =
    !!prog && (prog.global.items.length > 0 || prog.opportunities.some((o) => o.items.length > 0));

  return (
    <div className="pm-wia">
      <HelpPanel help={withFinalizedRule(ASSIGNMENT_HELP)} />
      <div className="wia-head">
        <div className="wia-titlerow">
          <i className="material-icons wia-titleicon">assignment_ind</i>
          <div>
            <h1 className="page-header__title">Work Item Assignment</h1>
            <p className="wia-sub">
              Assign people (name + email) to each in-scope work item — Global and per
              opportunity, by phase and stream.
            </p>
          </div>
        </div>

        <div className="dl-toolbar">
          <div className="dl-fg">
            <SelectField
              id="wia-project"
              label="Project"
              width="lg"
              placeholder={PROMPT.project}
              value={projectId}
              onChange={(v) => void onSelectProject(v)}
              options={projects.map((p) => ({
                value: p.delivery_project_id,
                id: p.delivery_project_id,
                description: p.description,
              }))}
            />
          </div>

          {hasRows ? (
            <div className="wia-actions">
              <button type="button" className="link-btn" onClick={() => expandAll(true)}>
                Expand all
              </button>
              <button type="button" className="link-btn" onClick={() => expandAll(false)}>
                Collapse all
              </button>
              <span style={{ color: '#cfd8dc' }}>|</span>
              <span className="dl-summary">{totalAssigned} assigned</span>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void save()}
                disabled={!dirty && saveLabel === 'Save'}
              >
                {saveLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="wia-body">
        {hasRows && prog ? (
          <table className="dg">
            <colgroup>
              <col style={{ width: 44 }} />
              <col />
            </colgroup>
            <tbody>
              {prog.global.items.length
                ? scopeRows(prog.global, '🌐 Global Work Items', '#6a1b9a')
                : null}
              {prog.opportunities.map((op) =>
                op.items.length
                  ? scopeRows(op, `📌 ${op.opportunity_id}`, '#1976d2', op)
                  : null,
              )}
            </tbody>
          </table>
        ) : (
          <div className="wia-empty">
            <i className="material-icons ic">assignment_ind</i>
            {emptyReason === 'no-items' ? (
              <>
                <h3>No In-Scope Work Items</h3>
                <p>
                  Mark work items in scope on the <strong>Work Item Definition</strong> screen
                  first.
                </p>
              </>
            ) : (
              <>
                <h3>Select a Project</h3>
                <p>
                  Pick a program above. Assignment covers the work items marked in scope on{' '}
                  <strong>Work Item Definition</strong>.
                </p>
              </>
            )}
            {notice ? (
              <p style={{ marginTop: 10, color: '#c62828', fontWeight: 600 }}>{notice}</p>
            ) : null}
          </div>
        )}
      </div>

      {/* The source's `<datalist id="peopleList">`, feeding the Name inputs. */}
      <datalist id="pm-wia-people">
        {people.map((p, i) => (
          <option key={i} value={p.name || p.email} />
        ))}
      </datalist>
    </div>
  );
}
